// Author.tsx
import { Box, Flex, Grid } from 'styled-system/jsx'
import { Avatar } from '../../../../features/Avatar'
import { circle } from 'styled-system/patterns'
import { AppTooltip, Rank, Text } from '@components'
import { createQuery } from '@tanstack/solid-query'
import { api } from '@here/api'
import dayjs from 'dayjs'

type AuthorProfile = { username: string | null; rank?: string } // type this

async function fetchAuthorProfile(id: string): Promise<AuthorProfile> {
	const sb = api()

	// Public username (+ public role as fallback)
	const { data: pub, error: pubErr } = await sb
		.from('profile')
		.select('username')
		.eq('id', id)
		.maybeSingle()

	if (pubErr) throw pubErr

	// Private role (preferred for "rank" if present and RLS allows)
	const { data: priv, error: privErr } = await sb
		.from('profile_private')
		.select('role')
		.eq('id', id)
		.maybeSingle()

	// It's fine if privErr exists due to RLS; we'll just ignore and fallback.

	return { username: pub?.username ?? null, rank: priv?.role }
}

export const Author = (props: { createdBy?: string; created_at?: string }) => {
	const authorQuery = createQuery(() => ({
		queryKey: ['author', props.createdBy],
		queryFn: () => fetchAuthorProfile(props.createdBy!),
		enabled: !!props.createdBy,
		// Cache for 1 day (24 hours)
		staleTime: 24 * 60 * 60 * 1000, // 24 hours
		gcTime: 24 * 60 * 60 * 1000, // 24 hours
		retry: 1,
	}))

	const username = () =>
		authorQuery.data?.username ?? (props.createdBy ? 'Unknown' : '—')
	const rank = () => authorQuery.data?.rank ?? 'Unranked'

	return (
		<Flex
			alignItems="center"
			gap={4}
		>
			<Box pos="relative">
				<Avatar id={props.createdBy} />
				<Box
					pos="absolute"
					top={0}
					right={-2}
				>
					<AppTooltip
						content={rank()}
						positioning={{ placement: 'top' }}
					>
						<Rank
							dimensions="20px"
							rank={rank()}
						/>
					</AppTooltip>
				</Box>
			</Box>

			<Grid gap={0}>
				<Flex
					alignItems="center"
					gap={1}
				>
					<Text
						color="neutralSubtle"
						size="sm"
            textTransform='capitalize'
					>
						{authorQuery.isLoading
							? 'Loading…'
							: authorQuery.isError
								? 'Error'
								: username()}
					</Text>
				</Flex>
				<Text
					color="neutralSubtle"
					size="xs"
				>
					{dayjs(props.created_at).format('DD MMMM, YYYY')}
				</Text>
			</Grid>
		</Flex>
	)
}
